import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Minus, Plus, Search, ShoppingCart, Star, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProductImage } from '@/components/catalog/ProductImage';
import { formatUsd } from '@/lib/format';
import type { Category } from '@/types/domain';
import {
  stableHash,
  useDeskCatalog,
  useDeskSubmitOrder,
  type DeskItem,
  type DeskLocation,
  type DeskOrderPreview,
  type DeskOrderPlaced,
} from '@/hooks/usePhoneDesk';
import { fmtDay } from './deskFormat';

/**
 * The phone desk's "New order" flow — three steps on one panel:
 *
 *   1. Build: pick the location, delivery day, items (their usual items first),
 *      at the customer's own prices.
 *   2. Review: submit-order DRY RUN — the server prices it and adds the fees, so
 *      what the receptionist reads back is what will be placed.
 *   3. Place: the real submit-order call, tagged phone_desk. Service Fusion job,
 *      order record and the customer's own confirmation email happen exactly as
 *      for a portal order.
 *
 * The idempotency key is fixed for the life of this panel, so a double click or
 * a retry after a network blip can never place the order twice.
 */

const FALLBACK_ACCENT = 'var(--brix-primary)';

export function DeskOrderPanel({
  customerId,
  customerName,
  locations,
  initialLocationId,
  onClose,
}: {
  customerId: string;
  customerName: string;
  locations: DeskLocation[];
  initialLocationId: string;
  onClose: () => void;
}) {
  const catalog = useDeskCatalog(customerId, true);
  const submit = useDeskSubmitOrder();
  const nonce = useRef(Math.random().toString(36).slice(2, 10));

  const [locationId, setLocationId] = useState(initialLocationId);
  const location = locations.find((l) => l.id === locationId) ?? locations[0]!;
  const [date, setDate] = useState(location.next_delivery_date ?? '');
  const [po, setPo] = useState('');
  const [note, setNote] = useState('');
  const [find, setFind] = useState('');
  const [cart, setCart] = useState<Record<string, number>>({});
  const [step, setStep] = useState<'build' | 'review' | 'placed'>('build');
  const [preview, setPreview] = useState<DeskOrderPreview | null>(null);
  const [placed, setPlaced] = useState<DeskOrderPlaced | null>(null);
  const [error, setError] = useState<string | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  // On a phone the read-back sits below the item list — bring it into view.
  useEffect(() => {
    if (step === 'review') asideRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [step]);

  const items = catalog.data?.items ?? [];
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i] as const)), [items]);
  const usual = (catalog.data?.usual ?? []).map((id) => byId.get(id)).filter((i): i is DeskItem => !!i);
  const term = find.trim().toLowerCase();
  const found = term
    ? items
        .filter((i) => i.name.toLowerCase().includes(term) || (i.sku ?? '').toLowerCase().includes(term) || (i.category ?? '').toLowerCase().includes(term))
        .slice(0, 30)
    : [];

  const lines = Object.entries(cart).filter(([, q]) => q > 0);
  const estimate = lines.reduce((sum, [id, q]) => sum + (byId.get(id)?.price ?? 0) * q, 0);

  function setQty(id: string, q: number) {
    setCart((c) => ({ ...c, [id]: Math.max(0, Math.min(999, Math.floor(q) || 0)) }));
    if (step === 'review') setStep('build');
  }

  function payload(dryRun: boolean) {
    const ls = lines.map(([catalog_item_id, quantity]) => ({ catalog_item_id, quantity }));
    const fingerprint = JSON.stringify([locationId, date, po, note, ls]);
    return {
      customer_id: customerId,
      customer_location_id: locationId,
      lines: ls,
      po_number: po.trim() || undefined,
      delivery_notes: note.trim() || undefined,
      requested_delivery_date: date || undefined,
      idempotency_key: `desk:${customerId}:${nonce.current}:${stableHash(fingerprint)}`,
      dry_run: dryRun,
    };
  }

  function explain(err: Error & { code?: string }): string {
    if (err.code === 'ordering_locked') return 'Ordering is paused on this account. Do not take the order — pass the call to the office.';
    if (err.code === 'account_inactive') return 'This account is not active. Pass the call to the office.';
    return err.message || 'Something went wrong.';
  }

  async function review() {
    setError(null);
    try {
      const res = await submit.mutateAsync(payload(true));
      setPreview(res);
      setStep('review');
    } catch (e) {
      setError(explain(e as Error & { code?: string }));
    }
  }
  async function place() {
    setError(null);
    try {
      const res = await submit.mutateAsync(payload(false));
      setPlaced(res);
      setStep('placed');
    } catch (e) {
      setError(explain(e as Error & { code?: string }));
    }
  }

  return (
    <div className="rounded-xl border-2 border-brix-primary/40 bg-brix-surface shadow-card-hover" role="region" aria-label="New order">
      <div className="flex items-center gap-2 border-b border-brix-border px-4 py-3">
        <ShoppingCart size={16} className="text-brix-primary" aria-hidden />
        <h2 className="font-display text-base font-bold text-brix-ink">New order for {customerName}</h2>
        <span className="ml-2 hidden text-xs text-brix-muted sm:inline">
          {step === 'build' ? 'Step 1 of 2 — build' : step === 'review' ? 'Step 2 of 2 — read it back' : 'Placed'}
        </span>
        <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-brix-muted hover:bg-brix-surface-alt hover:text-brix-ink" aria-label="Close (Esc)">
          <X size={16} />
        </button>
      </div>

      {step === 'placed' && placed ? (
        <div className="px-4 py-8 text-center">
          <CheckCircle2 size={36} className="mx-auto text-brix-success" aria-hidden />
          <p className="mt-3 font-display text-lg font-bold text-brix-ink">Order placed</p>
          <p className="mt-1 text-sm text-brix-muted">Read the order number to the caller:</p>
          <p className="mt-2 font-mono text-2xl font-bold tracking-wide text-brix-primary">{placed.order_number}</p>
          <p className="mx-auto mt-3 max-w-md text-sm text-brix-muted">
            {placed.idempotent_replay
              ? 'This order was already placed — nothing new was created.'
              : `Delivery will be scheduled${date ? ` for ${fmtDay(date)} or the next route day` : ''}. ${placed.email_sent ? 'A confirmation email is on its way to the account’s contacts.' : ''}`}
          </p>
          <Button className="mt-5" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          {/* Left: details + item picker */}
          <div className="min-w-0 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {locations.length > 1 && (
                <label className="block text-xs font-semibold text-brix-muted sm:col-span-2">
                  Deliver to
                  <select
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      const l = locations.find((x) => x.id === e.target.value);
                      setDate(l?.next_delivery_date ?? '');
                      setStep('build');
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-brix-border bg-brix-surface px-3 text-sm font-normal text-brix-ink focus:border-brix-primary focus:outline-none"
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ''}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="block text-xs font-semibold text-brix-muted">
                Delivery day
                <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setStep('build'); }} className="mt-1" />
                <span className="mt-0.5 block font-normal">
                  {location.delivery_days ? `Route: ${location.delivery_days}` : 'Dispatch confirms the day.'}
                </span>
              </label>
              <label className="block text-xs font-semibold text-brix-muted">
                PO number (if they give one)
                <Input value={po} maxLength={40} onChange={(e) => { setPo(e.target.value); setStep('build'); }} className="mt-1" />
              </label>
              <label className="block text-xs font-semibold text-brix-muted sm:col-span-2">
                Delivery note
                <Input value={note} maxLength={300} placeholder="e.g. Back door, ask for Maria" onChange={(e) => { setNote(e.target.value); setStep('build'); }} className="mt-1" />
              </label>
            </div>

            <div>
              <div className="relative">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" aria-hidden />
                <Input
                  autoFocus
                  value={find}
                  onChange={(e) => setFind(e.target.value)}
                  placeholder="Find a product — flavor, size or code"
                  className="pl-9"
                  aria-label="Find a product"
                />
              </div>
              {catalog.isLoading ? (
                <p className="flex items-center gap-2 py-6 text-sm text-brix-muted"><Loader2 size={14} className="animate-spin" /> Loading products…</p>
              ) : catalog.isError ? (
                <p className="py-6 text-sm text-brix-warning">Could not load products.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {!term && usual.length > 0 && (
                    <ItemList title="Their usual items" icon={<Star size={13} aria-hidden />} items={usual} cart={cart} setQty={setQty} />
                  )}
                  {term && <ItemList title={`Matches (${found.length})`} items={found} cart={cart} setQty={setQty} />}
                  {!term && usual.length === 0 && (
                    <p className="text-sm text-brix-muted">No recent orders — search for what they want.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: the order so far / the read-back */}
          <aside ref={asideRef} className="min-w-0 space-y-3 rounded-lg border border-brix-border bg-brix-surface-alt p-3 lg:self-start">
            {step === 'review' && preview ? (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-brix-muted">Read this back</p>
                <p className="text-sm text-brix-ink">
                  Deliver to <span className="font-semibold">{location.name}</span>
                  {date ? <> on <span className="font-semibold">{fmtDay(date)}</span></> : null}
                  {po.trim() ? <> · PO {po.trim()}</> : null}
                </p>
                <ul className="space-y-1 text-sm">
                  {preview.order_lines.map((l) => (
                    <li key={l.catalog_item_id} className="flex justify-between gap-2">
                      <span className="min-w-0 text-brix-ink">{l.quantity} × {l.display_name}</span>
                      <span className="shrink-0 font-mono text-brix-ink">{formatUsd(l.line_total)}</span>
                    </li>
                  ))}
                  {preview.fee_lines.map((f, i) => (
                    <li key={`fee-${i}`} className="flex justify-between gap-2 text-brix-muted">
                      <span className="min-w-0">{f.name}</span>
                      <span className="shrink-0 font-mono">{formatUsd(f.amount)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between border-t border-brix-border pt-2 text-sm font-semibold text-brix-ink">
                  <span>Estimated total</span>
                  <span className="font-mono">{formatUsd(preview.totals.estimated_total)}</span>
                </div>
                {(preview.cylinder_warnings ?? []).length > 0 && (
                  <p className="flex items-start gap-1.5 rounded border border-brix-warning/40 bg-brix-surface px-2 py-1.5 text-xs text-brix-ink">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0 text-brix-warning" aria-hidden />
                    The records show fewer tanks at this location than they are returning. Place the order anyway — the office will check the count.
                  </p>
                )}
                <p className="text-xs text-brix-muted">Prices are the account’s own. Tax, if any, is added on the invoice.</p>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setStep('build')} disabled={submit.isPending}>Back</Button>
                  <Button className="flex-1" onClick={() => void place()} disabled={submit.isPending}>
                    {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} aria-hidden />}
                    Yes — place order
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-brix-muted">Order</p>
                {lines.length === 0 ? (
                  <p className="text-sm text-brix-muted">Nothing added yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {lines.map(([id, q]) => {
                      const it = byId.get(id);
                      return (
                        <li key={id} className="flex items-center gap-2 text-sm">
                          <span className="min-w-0 flex-1 truncate text-brix-ink">{it?.name ?? 'Item'}</span>
                          <Stepper value={q} onChange={(n) => setQty(id, n)} label={it?.name ?? 'item'} />
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="flex justify-between border-t border-brix-border pt-2 text-sm text-brix-ink">
                  <span>Items</span>
                  <span className="font-mono">{formatUsd(estimate)}</span>
                </div>
                <p className="text-xs text-brix-muted">Fees are added on the next step.</p>
                <Button className="w-full" onClick={() => void review()} disabled={lines.length === 0 || submit.isPending}>
                  {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                  Review order
                </Button>
              </>
            )}
            {error && (
              <p role="alert" className="flex items-start gap-1.5 rounded border border-brix-warning/40 bg-brix-surface px-2 py-1.5 text-xs text-brix-ink">
                <AlertTriangle size={13} className="mt-0.5 shrink-0 text-brix-warning" aria-hidden /> {error}
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function ItemList({
  title,
  icon,
  items,
  cart,
  setQty,
}: {
  title: string;
  icon?: React.ReactNode;
  items: DeskItem[];
  cart: Record<string, number>;
  setQty: (id: string, q: number) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-brix-muted">
        {icon} {title}
      </p>
      <ul className="divide-y divide-brix-border overflow-hidden rounded-lg border border-brix-border">
        {items.map((i) => (
          <li key={i.id} className="flex items-center gap-3 bg-brix-surface px-3 py-2">
            <ProductImage
              imageUrl={i.image}
              accent={i.accent ?? FALLBACK_ACCENT}
              category={(i.category ?? 'Other') as Category}
              alt={i.name}
              size="sm"
              zoomOnHover={false}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-brix-ink">{i.name}</p>
              <p className="truncate text-xs text-brix-muted">
                {[i.category, i.pack_size, i.sku].filter(Boolean).join(' · ')}
              </p>
            </div>
            <span className="hidden shrink-0 font-mono text-sm text-brix-ink sm:inline">{formatUsd(i.price)}</span>
            {(cart[i.id] ?? 0) > 0 ? (
              <Stepper value={cart[i.id] ?? 0} onChange={(n) => setQty(i.id, n)} label={i.name} />
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setQty(i.id, 1)} aria-label={`Add ${i.name}`}>
                <Plus size={13} aria-hidden /> Add
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stepper({ value, onChange, label }: { value: number; onChange: (n: number) => void; label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-lg border border-brix-border bg-brix-surface">
      <button type="button" className="flex h-8 w-8 items-center justify-center text-brix-ink hover:bg-brix-primary-tint" onClick={() => onChange(value - 1)} aria-label={`One less ${label}`}>
        <Minus size={13} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-8 w-11 border-x border-brix-border bg-transparent text-center font-mono text-sm text-brix-ink focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        aria-label={`Quantity of ${label}`}
      />
      <button type="button" className="flex h-8 w-8 items-center justify-center text-brix-ink hover:bg-brix-primary-tint" onClick={() => onChange(value + 1)} aria-label={`One more ${label}`}>
        <Plus size={13} />
      </button>
    </span>
  );
}
